import React, {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {Box, Stack} from '@mui/material';
import {Body, Headline, Icon} from '@lightricks/react-design-system';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import PoppaysLoader from '@/components/poppays-loader';
import useParams from '@/hooks/use-params';
import translate from '@/utils/translate';
import GigLoadError from '../components/GigLoadError';
import GigShell from '../shell/GigShell';
import {GigTab} from '../shell/gigTabs';
import useGigShellData from '../shell/useGigShellData';
import {captureDeliverablesError, trackDeliverables} from '../telemetry';
import CopyableValue from '../brief/CopyableValue';
import AffiliateStats from './AffiliateStats';
import discountDisplay, {NO_VALUE} from './discountDisplay';
import styles from './AffiliateIncomePage.module.scss';

dayjs.extend(relativeTime);

export default function AffiliateIncomePage() {
  const {gigId} = useParams();
  const navigate = useNavigate();

  const {
    shell,
    affiliate,
    deliverables,
    totals,
    error,
    gigLoading,
    gigFailed,
    submissionsLoading,
    submissionsFailed,
    refetch,
  } = useGigShellData(gigId);

  useEffect(() => {
    trackDeliverables('Opened affiliate income', {gigId});
  }, [gigId]);

  useEffect(() => {
    if (gigFailed || submissionsFailed) {
      captureDeliverablesError(error, 'Affiliate income failed to load', {
        gigId,
        gigFailed,
        submissionsFailed,
      });
    }
  }, [error, gigFailed, submissionsFailed, gigId]);

  if (gigLoading) {
    return <PoppaysLoader />;
  }

  if (gigFailed) {
    return <GigLoadError onRetry={refetch} />;
  }

  const handleSelectKind = (tab: GigTab) => {
    if (tab.to) {
      navigate(tab.to);
    }
  };

  const expiresAt = dayjs(affiliate.expiresAt);
  const lastUpdated = dayjs(affiliate.lastProcessedOrderAt);
  const showExpiry = Boolean(affiliate.expiresAt) && expiresAt.isValid();
  const showLastUpdated =
    Boolean(affiliate.lastProcessedOrderAt) && lastUpdated.isValid();

  return (
    <GigShell
      gigId={gigId}
      activeKind="affiliate-income"
      shell={shell}
      deliverables={deliverables}
      totals={totals}
      showProgress={!submissionsLoading && !submissionsFailed}
      onSelectKind={handleSelectKind}
    >
      <Box className={styles.page}>
        <Stack
          component="section"
          alignItems="center"
          spacing={5}
          maxWidth={420}
          textAlign="center"
          data-testid="affiliate-summary"
        >
          <Stack alignItems="center" spacing={2}>
            <Icon size="large" appearance="brand" name="Actions-Trending" />
            <Headline size="md" component="h1">
              {translate('Affiliate Income')}
            </Headline>
            <Body size="md" component="div" className={styles.terms}>
              <div data-testid="affiliate-discount">
                <strong>{translate('Discount:')}</strong>{' '}
                {discountDisplay(
                  affiliate.discountValue,
                  affiliate.discountValueType
                )}
              </div>
              {showExpiry ? (
                <div data-testid="affiliate-expires">
                  <strong>{translate('Expires:')}</strong>{' '}
                  {expiresAt.format('MM/DD/YYYY')}
                </div>
              ) : null}
            </Body>
          </Stack>

          {affiliate.discountCode ? (
            <CopyableValue
              testID="affiliate-discount-code"
              label={translate('Your discount code')}
              value={affiliate.discountCode}
            />
          ) : (
            <Body
              size="md"
              component="div"
              className={styles.terms}
              data-testid="affiliate-discount-code"
            >
              <strong>{translate('Your discount code:')}</strong> {NO_VALUE}
            </Body>
          )}

          <AffiliateStats
            affiliate={affiliate}
            updatedLabel={
              showLastUpdated
                ? translate('Updated {timeAgo}', {
                    timeAgo: lastUpdated.fromNow(),
                  })
                : null
            }
          />
        </Stack>
      </Box>
    </GigShell>
  );
}
